import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  tableService,
  zoneService,
  type RestaurantTable,
  type RestaurantZone,
  type TableStatus,
} from '@resort-os/api-client';
import {
  FormDialog,
  FormTextField,
  FormSelect,
  FormSection,
  FormButton,
  PageSection,
} from '@resort-os/ui';
import { posTableModalTour, POS_TABLE_MODAL_TOUR_KEY } from '../tours/posModalTours';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Divider,
} from '@mui/material';
import {
  TableRestaurant as TableIcon,
  People as PeopleIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  EventSeat as EventSeatIcon,
  CleaningServices as CleaningIcon,
  GridView as GridIcon,
  Map as MapIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';

const ACCENT = '#2C3E2D';
const BORDER = '1px solid #E2DDD8';
const SANS = '"DM Sans", system-ui, sans-serif';
const SERIF = '"Playfair Display", Georgia, serif';
const MUTED = '#74706B';
const FAINT = '#B8B3AD';
const INK = '#0C0C0B';
const PAPER = '#FAFAF7';
const RED = '#BF360C';
const ORANGE = '#E65100';
const BLUE = '#1565C0';

const STATUS_CONFIG: Record<
  TableStatus,
  {
    label: string;
    icon: React.ReactElement;
    description: string;
    chipBg?: string;
    chipColor?: string;
  }
> = {
  available: {
    label: 'Available',
    icon: <CheckIcon sx={{ fontSize: 16 }} />,
    description: 'Ready for guests',
    chipBg: '#E8F5E9',
    chipColor: '#2E7D32',
  },
  reserved: {
    label: 'Reserved',
    icon: <EventSeatIcon sx={{ fontSize: 16 }} />,
    description: 'Booked for later',
    chipBg: '#FAFAF8',
    chipColor: '#ED6C02',
  },
  occupied: {
    label: 'Occupied',
    icon: <PeopleIcon sx={{ fontSize: 16 }} />,
    description: 'Currently serving',
    chipBg: '#FAFAF8',
    chipColor: '#D32F2F',
  },
  cleaning: {
    label: 'Cleaning',
    icon: <CleaningIcon sx={{ fontSize: 16 }} />,
    description: 'Being prepared',
    chipBg: '#FAFAF8',
    chipColor: '#0288D1',
  },
};

interface TableFormData {
  table_number: string;
  display_name: string;
  capacity: number;
  status: TableStatus;
  notes: string;
  zone_id: string;
}

interface TablesPageProps {
  PageHeader: React.FC<{ title: string }>;
}

function TablesPage({ PageHeader }: TablesPageProps) {
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [zoneFilter, setZoneFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'floorplan'>('grid');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<RestaurantTable | null>(null);
  const [selectedTable, setSelectedTable] = useState<RestaurantTable | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeTableForStatus, setActiveTableForStatus] = useState<RestaurantTable | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TableFormData>({
    defaultValues: {
      table_number: '',
      display_name: '',
      capacity: 4,
      status: 'available',
      notes: '',
      zone_id: '',
    },
  });

  const { data: tablesData, isLoading } = useQuery({
    queryKey: ['tables'],
    queryFn: () => tableService.list({}),
  });

  const { data: zonesData } = useQuery({
    queryKey: ['tables-zones'],
    queryFn: () => zoneService.getAll(),
    staleTime: 0,
  });

  const zones: RestaurantZone[] = (() => {
    if (Array.isArray(zonesData)) return zonesData;
    const data = (zonesData as any)?.data;
    if (Array.isArray(data)) return data;
    return [];
  })();
  const allTables = tablesData?.data?.data || [];

  const filteredTables = React.useMemo(() => {
    let result = allTables;
    if (statusFilter !== 'all') {
      result = result.filter((t: RestaurantTable) => t.status === statusFilter);
    }
    if (zoneFilter !== 'all') {
      result = result.filter((t: RestaurantTable) => t.zone_id === zoneFilter);
    }
    return result;
  }, [allTables, statusFilter, zoneFilter]);

  const tablesByZone = React.useMemo(() => {
    const grouped: Record<string, RestaurantTable[]> = {};
    allTables.forEach((t: RestaurantTable) => {
      const zid = t.zone_id || '__none__';
      if (!grouped[zid]) grouped[zid] = [];
      grouped[zid].push(t);
    });
    return grouped;
  }, [allTables]);

  const createMutation = useMutation({
    mutationFn: (data: Partial<RestaurantTable>) => tableService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      setDialogOpen(false);
      setEditingTable(null);
      reset();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RestaurantTable> }) =>
      tableService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      setDialogOpen(false);
      setEditingTable(null);
      reset();
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TableStatus }) =>
      tableService.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      setStatusMenuAnchor(null);
      setActiveTableForStatus(null);
    },
  });

  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<RestaurantZone | null>(null);
  const [zoneForm, setZoneForm] = useState({ name: '', code: '', description: '' });

  const zoneCreateMutation = useMutation({
    mutationFn: (data: Partial<RestaurantZone>) => zoneService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      setZoneDialogOpen(false);
      setEditingZone(null);
      setZoneForm({ name: '', code: '', description: '' });
    },
  });

  const zoneUpdateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RestaurantZone> }) =>
      zoneService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      setZoneDialogOpen(false);
      setEditingZone(null);
      setZoneForm({ name: '', code: '', description: '' });
    },
  });

  const zoneDeleteMutation = useMutation({
    mutationFn: (id: string) => zoneService.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['zones'] }),
  });

  const openZoneDialog = (zone?: RestaurantZone) => {
    if (zone) {
      setEditingZone(zone);
      setZoneForm({ name: zone.name, code: zone.code || '', description: zone.description || '' });
    } else {
      setEditingZone(null);
      setZoneForm({ name: '', code: '', description: '' });
    }
    setZoneDialogOpen(true);
  };

  const tables = filteredTables;
  const statusCounts = React.useMemo(() => {
    const allTables = tablesData?.data?.data || [];
    const counts: Record<string, number> = { all: allTables.length };
    allTables.forEach((table: RestaurantTable) => {
      counts[table.status] = (counts[table.status] || 0) + 1;
    });
    return counts;
  }, [tablesData]);

  const onSubmit = (data: TableFormData) => {
    const payload = {
      table_number: data.table_number,
      display_name: data.display_name || data.table_number,
      capacity: data.capacity,
      status: data.status,
      notes: data.notes,
      zone_id: data.zone_id || undefined,
    };
    if (editingTable) {
      updateMutation.mutate({ id: editingTable.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const openDialog = (table?: RestaurantTable) => {
    if (table) {
      setEditingTable(table);
      reset({
        table_number: table.table_number,
        display_name: table.display_name,
        capacity: table.capacity,
        status: table.status,
        notes: table.notes || '',
        zone_id: table.zone_id || '',
      });
    } else {
      setEditingTable(null);
      reset({
        table_number: '',
        display_name: '',
        capacity: 4,
        status: 'available',
        notes: '',
        zone_id: '',
      });
    }
    setDialogOpen(true);
  };

  const handleStatusMenuOpen = (event: React.MouseEvent<HTMLElement>, table: RestaurantTable) => {
    event.stopPropagation();
    setStatusMenuAnchor(event.currentTarget);
    setActiveTableForStatus(table);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setActiveTableForStatus(null);
  };

  const handleStatusChange = (status: TableStatus) => {
    if (activeTableForStatus) {
      updateStatusMutation.mutate({ id: activeTableForStatus.id, status });
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: '#FAF9F7',
        }}
      >
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative' }}>
      <PageSection>
        <PageHeader title="Tables" />

        {/* Floating vertical status filter — right side */}
        <Box
          data-tour="pos-tables-filters"
          sx={{
            position: 'fixed',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            zIndex: 1200,
            p: 1.25,
            width: 170,
            bgcolor: 'rgba(255,255,255,0.96)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          {[
            { id: 'all', label: 'All', color: ACCENT },
            ...Object.entries(STATUS_CONFIG).map(([status, config]) => ({
              id: status,
              label: config.label,
              color: config.chipColor || ACCENT,
            })),
          ].map(item => {
            const isActive = statusFilter === item.id;
            return (
              <Box
                key={item.id}
                onClick={() => setStatusFilter(item.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.75,
                  py: 1.25,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  bgcolor: isActive ? `${item.color}15` : 'transparent',
                  border: isActive ? `1px solid ${item.color}40` : '1px solid transparent',
                  '&:hover': { bgcolor: `${item.color}08` },
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    bgcolor: isActive ? item.color : '#F5F5F5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: isActive ? '#FFF' : '#9B958F',
                    }}
                  >
                    {statusCounts[item.id] || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: isActive ? item.color : '#1A1A1A',
                      lineHeight: 1.2,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: '#9B958F',
                    }}
                  >
                    tables
                  </Typography>
                </Box>
                {isActive && (
                  <Box
                    sx={{
                      width: 3,
                      height: 20,
                      borderRadius: 1.5,
                      bgcolor: item.color,
                      alignSelf: 'stretch',
                      my: -0.5,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        <Box
          data-tour="pos-tables-toolbar"
          sx={{
            display: 'flex',
            pt: 2,
            pb: 3,
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#6B6560' }}>Zone:</Typography>
            <select
              value={zoneFilter}
              onChange={e => setZoneFilter(e.target.value)}
              style={{
                fontSize: '0.75rem',
                padding: '6px 12px',
                border: '1px solid #E2DDD8',
                borderRadius: 0,
                background: '#FFF',
                minWidth: 120,
                cursor: 'pointer',
              }}
            >
              <option value="all">All Zones ({allTables.length})</option>
              {zones.map(z => (
                <option key={z.id} value={z.id}>
                  {z.name} ({allTables.filter((t: RestaurantTable) => t.zone_id === z.id).length})
                </option>
              ))}
            </select>
            <Button
              size="small"
              onClick={() => openZoneDialog()}
              sx={{
                fontSize: '0.7rem',
                color: ACCENT,
                textTransform: 'none',
              }}
            >
              + Manage Zones
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', border: BORDER, borderRadius: 0, overflow: 'hidden' }}>
              <Button
                onClick={() => setViewMode('grid')}
                sx={{
                  borderRadius: 0,
                  px: 1.5,
                  py: 0.75,
                  minWidth: 'auto',
                  bgcolor: viewMode === 'grid' ? ACCENT : 'transparent',
                  color: viewMode === 'grid' ? '#FFF' : '#6B6560',
                  borderRight: BORDER,
                  '&:hover': { bgcolor: viewMode === 'grid' ? ACCENT : '#F5F4F2' },
                }}
              >
                <GridIcon sx={{ fontSize: 18 }} />
              </Button>
              <Button
                onClick={() => setViewMode('floorplan')}
                sx={{
                  borderRadius: 0,
                  px: 1.5,
                  py: 0.75,
                  minWidth: 'auto',
                  bgcolor: viewMode === 'floorplan' ? ACCENT : 'transparent',
                  color: viewMode === 'floorplan' ? '#FFF' : '#6B6560',
                  '&:hover': { bgcolor: viewMode === 'floorplan' ? ACCENT : '#F5F4F2' },
                }}
              >
                <MapIcon sx={{ fontSize: 18 }} />
              </Button>
            </Box>
            <Button
              variant="contained"
              onClick={() => openDialog()}
              startIcon={<TableIcon />}
              sx={{
                bgcolor: ACCENT,
                borderRadius: 0,
                px: 3,

                fontSize: '0.8rem',
                '&:hover': { bgcolor: '#1a2d1b' },
              }}
            >
              Add Table
            </Button>
          </Box>
        </Box>

        {/* Tables Grid / Floor Plan */}
        <Box data-tour="pos-tables-grid">
          {filteredTables.length === 0 ? (
            <Paper
              sx={{
                textAlign: 'center',
                py: 8,
                border: BORDER,
                borderRadius: 0,
                boxShadow: 'none',
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: '#FAFAF8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TableIcon sx={{ fontSize: 40, color: '#C4BEB8' }} />
              </Box>
              <Typography sx={{ color: '#9B958F' }}>No tables found</Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#9B958F',
                  mt: 1,
                }}
              >
                Click "Add Table" to create your first table
              </Typography>
            </Paper>
          ) : viewMode === 'floorplan' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                {Object.entries(STATUS_CONFIG).map(([k, c]) => (
                  <Box
                    key={k}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '7px',

                      fontSize: '0.7rem',
                      color: MUTED,
                    }}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        border: `2px solid ${c.chipColor || ACCENT}`,
                        bgcolor: k === 'available' ? 'rgba(44,62,45,0.06)' : 'transparent',
                        flexShrink: 0,
                      }}
                    />
                    {c.label}
                  </Box>
                ))}
              </Box>
              {(zoneFilter === 'all' ? zones : zones.filter(z => z.id === zoneFilter)).map(zone => {
                const zoneTables = tablesByZone[zone.id] || [];
                if (zoneTables.length === 0) return null;
                return (
                  <Paper
                    key={zone.id}
                    elevation={0}
                    sx={{ p: 2, border: BORDER, borderRadius: 0, bgcolor: '#FAFAF8' }}
                  >
                    <Typography
                      sx={{
                        fontSize: '1rem',
                        color: ACCENT,
                        mb: 2,
                        fontWeight: 600,
                      }}
                    >
                      {zone.name}
                    </Typography>
                    <Box
                      sx={{
                        minHeight: 200,
                        bgcolor: '#FFF',
                        border: '1px dashed #E2DDD8',
                        p: 2,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'flex-start',
                        alignContent: 'flex-start',
                      }}
                    >
                      {zoneTables.map((table, idx) => {
                        const config = STATUS_CONFIG[table.status];
                        return (
                          <Box
                            key={table.id}
                            onClick={() => openDialog(table)}
                            onContextMenu={e => handleStatusMenuOpen(e, table)}
                            sx={{
                              width: table.width || 80,
                              height: table.height || 80,
                              bgcolor: config.chipBg || '#FFF',
                              border: `2px solid ${config.chipColor || ACCENT}`,
                              borderRadius: table.shape === 'circle' ? '50%' : 2,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                transform: 'scale(1.05)',
                                zIndex: 10,
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                color: config.chipColor || ACCENT,
                              }}
                            >
                              {table.table_number}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.6rem',
                                color: '#6B6560',
                              }}
                            >
                              {table.capacity}p
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '3px 64px 1.4fr 130px 150px 130px 90px',
                  alignItems: 'center',
                  py: 0,
                  pb: 0.5,
                  mb: '2px',
                  borderBottom: '1px solid #E4DFD9',
                }}
              >
                <Box />
                <Typography
                  sx={{
                    fontSize: '0.52rem',
                    fontWeight: 600,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: FAINT,
                    px: '12px',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.52rem',
                    fontWeight: 600,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: FAINT,
                    px: '12px',
                  }}
                >
                  Table
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.52rem',
                    fontWeight: 600,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: FAINT,
                    px: '12px',
                  }}
                >
                  Zone
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.52rem',
                    fontWeight: 600,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: FAINT,
                    px: '12px',
                  }}
                >
                  Status
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.52rem',
                    fontWeight: 600,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: FAINT,
                    px: '12px',
                  }}
                >
                  Capacity
                </Typography>
                <Box />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {filteredTables.map((table: RestaurantTable) => {
                  const statusConfig = STATUS_CONFIG[table.status];
                  const tableZone = zones.find(z => z.id === table.zone_id);
                  const stripeColor =
                    {
                      available: ACCENT,
                      reserved: '#E65100',
                      occupied: '#BF360C',
                      cleaning: '#1565C0',
                    }[table.status] || ACCENT;
                  return (
                    <Box
                      key={table.id}
                      onClick={() => openDialog(table)}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '3px 64px 1.4fr 130px 150px 130px 90px',
                        alignItems: 'center',
                        bgcolor: '#FFF',
                        border: BORDER,
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'border-color 0.12s',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 3,
                          bgcolor: stripeColor,
                        },
                        '&:hover': { borderColor: 'rgba(44,62,45,0.14)' },
                        '&:hover .row-actions': { opacity: 1, pointerEvents: 'all' },
                      }}
                    >
                      <Box />
                      <Box sx={{ p: '12px 8px 12px 10px' }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: PAPER,
                          }}
                        >
                          <TableIcon sx={{ fontSize: 20, color: stripeColor }} />
                        </Box>
                      </Box>
                      <Box sx={{ p: '12px' }}>
                        <Typography
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 700,
                            color: INK,
                            lineHeight: 1.2,
                          }}
                        >
                          {table.display_name || table.table_number}
                        </Typography>
                        <Typography sx={{ fontSize: '0.66rem', color: FAINT, mt: '1px' }}>
                          Table #{table.table_number}
                        </Typography>
                      </Box>
                      <Box sx={{ p: '12px' }}>
                        <Typography
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 500,
                            color: MUTED,
                          }}
                        >
                          {tableZone?.name || '—'}
                        </Typography>
                      </Box>
                      <Box sx={{ p: '12px' }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            px: '10px',
                            py: '4px',

                            fontSize: '0.64rem',
                            fontWeight: 600,
                            letterSpacing: '0.06em',
                            whiteSpace: 'nowrap',
                            bgcolor:
                              {
                                available: 'rgba(44,62,45,0.09)',
                                reserved: 'rgba(230,81,0,0.08)',
                                occupied: 'rgba(191,54,12,0.08)',
                                cleaning: 'rgba(21,101,192,0.08)',
                              }[table.status] || PAPER,
                            color:
                              {
                                available: ACCENT,
                                reserved: '#C45000',
                                occupied: '#9B2000',
                                cleaning: BLUE,
                              }[table.status] || ACCENT,
                          }}
                        >
                          {statusConfig.icon}
                          {statusConfig.label}
                        </Box>
                      </Box>
                      <Box sx={{ p: '12px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                        <PeopleIcon sx={{ fontSize: 13, color: FAINT }} />
                        <Typography sx={{ fontSize: '0.78rem', color: MUTED }}>
                          <strong style={{ color: INK, fontWeight: 700 }}>{table.capacity}</strong>{' '}
                          seats
                        </Typography>
                      </Box>
                      <Box sx={{ p: 0, pr: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                        <Box
                          className="row-actions"
                          onClick={e => {
                            e.stopPropagation();
                            handleStatusMenuOpen(e, table);
                          }}
                          sx={{
                            display: 'flex',
                            gap: '2px',
                            opacity: 0,
                            transition: 'opacity 0.12s',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              px: 1.25,
                              py: 0.75,
                              bgcolor: 'transparent',
                              border: BORDER,
                              cursor: 'pointer',
                              color: MUTED,

                              fontSize: '0.66rem',
                              fontWeight: 500,
                              transition: 'all 0.1s',
                              '&:hover': { bgcolor: ACCENT, borderColor: ACCENT, color: '#FFF' },
                            }}
                          >
                            <SwapIcon sx={{ fontSize: 11 }} /> Change
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        {/* Status Change Menu */}
        <Menu
          anchorEl={statusMenuAnchor}
          open={Boolean(statusMenuAnchor)}
          onClose={handleStatusMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              borderRadius: 0,
              border: BORDER,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              minWidth: 180,
            },
          }}
        >
          {Object.entries(STATUS_CONFIG).map(([status, config]) => (
            <MenuItem
              key={status}
              onClick={() => handleStatusChange(status as TableStatus)}
              sx={{
                py: 1,
                px: 2,
                '&:hover': {
                  bgcolor: '#FAFAF8',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: '#6B6560' }}>{config.icon}</ListItemIcon>
              <ListItemText
                primary={config.label}
                secondary={config.description}
                primaryTypographyProps={{
                  sx: {
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#1A1A1A',
                  },
                }}
                secondaryTypographyProps={{
                  sx: {
                    fontSize: '0.6rem',
                    color: '#9B958F',
                  },
                }}
              />
            </MenuItem>
          ))}
        </Menu>

        <FormDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setEditingTable(null);
            reset();
          }}
          tourSteps={posTableModalTour}
          tourKey={POS_TABLE_MODAL_TOUR_KEY}
          title={editingTable ? 'Edit Table' : 'Add New Table'}
          isEditing={!!editingTable}
          maxWidth="sm"
        >
          <Box component="form" id="table-form" onSubmit={handleSubmit(onSubmit)}>
            <FormSection
              icon={<TableIcon sx={{ fontSize: 14 }} />}
              title="Table Details"
              description="Number, display name, and capacity"
            >
              <Box data-tour="modal-pos-table-details">
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <FormTextField
                    name="table_number"
                    control={control as any}
                    label="Table Number"
                    fullWidth
                  />
                  <FormTextField
                    name="display_name"
                    control={control as any}
                    label="Display Name"
                    fullWidth
                  />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                  <FormTextField
                    name="capacity"
                    control={control as any}
                    label="Capacity"
                    type="number"
                    fullWidth
                  />
                </Box>
              </Box>
              <Box data-tour="modal-pos-table-zone" sx={{ mt: 2 }}>
                <FormSelect
                  name="zone_id"
                  control={control as any}
                  label="Zone"
                  options={[
                    { value: '', label: 'None' },
                    ...zones.map(z => ({ value: z.id, label: z.name })),
                  ]}
                />
              </Box>
            </FormSection>
            <FormSection
              tourId="modal-pos-table-status"
              icon={<TableIcon sx={{ fontSize: 14 }} />}
              title="Status"
              description="Current table status"
            >
              <FormSelect
                name="status"
                control={control as any}
                label="Status"
                options={Object.entries(STATUS_CONFIG).map(([v, c]) => ({
                  value: v,
                  label: c.label,
                }))}
              />
              <FormTextField
                name="notes"
                control={control as any}
                label="Notes"
                multiline
                rows={2}
                fullWidth
                sx={{ mt: 2 }}
              />
            </FormSection>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              <FormButton
                onClick={() => {
                  setDialogOpen(false);
                  setEditingTable(null);
                  reset();
                }}
                variant="outlined"
              >
                Cancel
              </FormButton>
              <FormButton
                type="submit"
                form="table-form"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending
                  ? 'Saving...'
                  : editingTable
                    ? 'Update Table'
                    : 'Create Table'}
              </FormButton>
            </Box>
          </Box>
        </FormDialog>

        <FormDialog
          open={zoneDialogOpen}
          onClose={() => {
            setZoneDialogOpen(false);
            setEditingZone(null);
          }}
          title="Manage Zones"
          maxWidth="xs"
        >
          <Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#6B6560', mb: 2 }}>
              Dining areas for organizing tables
            </Typography>
            {zones.length === 0 ? (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#9B958F',
                  fontStyle: 'italic',
                }}
              >
                No zones created yet
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  mb: 2,
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                {zones.map(z => (
                  <Box
                    key={z.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      borderBottom: BORDER,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.78rem',
                          fontWeight: 500,
                        }}
                      >
                        {z.name}
                      </Typography>
                      {z.description && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#9B958F' }}>
                          {z.description}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        size="small"
                        onClick={() => openZoneDialog(z)}
                        sx={{
                          fontSize: '0.65rem',
                          color: ACCENT,
                          minWidth: 'auto',
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          if (confirm(`Delete "${z.name}"?`)) {
                            zoneDeleteMutation.mutate(z.id);
                          }
                        }}
                        sx={{
                          fontSize: '0.65rem',
                          color: '#D32F2F',
                          minWidth: 'auto',
                        }}
                      >
                        Del
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <TextField
              label="Zone Name"
              value={zoneForm.name}
              onChange={e => setZoneForm(p => ({ ...p, name: e.target.value }))}
              fullWidth
              size="small"
              sx={{ mb: 1.5 }}
            />
            <TextField
              label="Description"
              value={zoneForm.description}
              onChange={e => setZoneForm(p => ({ ...p, description: e.target.value }))}
              multiline
              rows={2}
              fullWidth
              size="small"
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              {editingZone && (
                <FormButton onClick={() => setEditingZone(null)} variant="outlined" type="button">
                  New
                </FormButton>
              )}
              <FormButton
                onClick={() => {
                  setZoneDialogOpen(false);
                  setEditingZone(null);
                }}
                variant="outlined"
                type="button"
              >
                Close
              </FormButton>
              <FormButton
                type="button"
                disabled={
                  !zoneForm.name.trim() ||
                  zoneCreateMutation.isPending ||
                  zoneUpdateMutation.isPending
                }
                onClick={() => {
                  if (!zoneForm.name.trim()) return;
                  if (editingZone) {
                    zoneUpdateMutation.mutate({ id: editingZone.id, data: zoneForm });
                  } else {
                    zoneCreateMutation.mutate(zoneForm);
                  }
                }}
              >
                {editingZone ? 'Update' : 'Create'} Zone
              </FormButton>
            </Box>
          </Box>
        </FormDialog>
      </PageSection>
    </Box>
  );
}

export default TablesPage;
